import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import API_CONFIG from '../config/api';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      } else {
        // Check if email is from a reputable provider
        const domain = formData.email.toLowerCase().split('@')[1];
        const reputableProviders = [
          // Gmail
          'gmail.com', 'googlemail.com',
          // Yahoo
          'yahoo.com', 'yahoo.co.uk', 'yahoo.ca', 'yahoo.in', 'yahoo.com.au',
          'yahoo.de', 'yahoo.fr', 'yahoo.es', 'yahoo.it', 'yahoo.co.jp',
          'ymail.com', 'rocketmail.com',
          // Outlook/Microsoft
          'outlook.com', 'outlook.in', 'outlook.co.uk', 'outlook.fr', 'outlook.de',
          'outlook.es', 'outlook.it', 'outlook.jp', 'hotmail.com', 'hotmail.co.uk',
          'hotmail.fr', 'hotmail.de', 'hotmail.es', 'hotmail.it',
          'live.com', 'live.co.uk', 'live.fr', 'live.de', 'live.in', 'msn.com',
          // Zoho
          'zoho.com', 'zohomail.com', 'zoho.in', 'zoho.eu',
          // Apple
          'icloud.com', 'me.com', 'mac.com',
          // ProtonMail
          'protonmail.com', 'proton.me', 'pm.me',
          // Other reputable providers
          'aol.com', 'mail.com', 'gmx.com', 'gmx.de', 'web.de',
          'yandex.com', 'yandex.ru', 'mail.ru',
          'rediffmail.com', 'rediff.com', 'sify.com', 'in.com', 'indiatimes.com'
        ];

        if (!reputableProviders.includes(domain)) {
          errors.email = 'Please use a reputable email provider (Gmail, Yahoo, Outlook, etc.)';
        }
      }
    }

    // Subject validation
    if (!formData.subject) {
      errors.subject = 'Please select a subject';
    }

    // Message validation
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setValidationErrors({});

    try {
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.contact.send}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus(null);
        }, 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - VestrioZ</title>
        <meta
          name="description"
          content="Get in touch with VestrioZ for support, inquiries, or feedback about our fashion rental platform."
        />
        <meta name="keywords" content="contact, support, help, VestrioZ" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-cream via-light-beige to-sage-green/20">
        <Navbar />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Information */}
            <div className="space-y-6 sm:space-y-8">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-dark-charcoal mb-3 sm:mb-4">
                  Contact Us
                </h1>
                <p className="text-base sm:text-lg text-medium-gray leading-relaxed">
                  We're here to help! If you have any questions, concerns, or need support
                  related to rentals, listings, payments, or refunds, feel free to reach out to us.
                </p>
              </div>

              {/* Business Information Card */}
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 border border-sage-green/10">
                <h2 className="text-xl sm:text-2xl font-semibold text-dark-charcoal mb-4 sm:mb-6">
                  Business Information
                </h2>

                <div className="space-y-4 sm:space-y-4">
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-sage-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-building text-sage-green text-base sm:text-lg"></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-dark-charcoal text-sm sm:text-base">Business Name</h3>
                      <p className="text-medium-gray text-sm sm:text-base">VestrioZ</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-sage-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-tag text-sage-green text-base sm:text-lg"></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-dark-charcoal text-sm sm:text-base">Business Type</h3>
                      <p className="text-medium-gray text-sm sm:text-base">Online platform to rent and sell clothes & jewellery</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-sage-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-envelope text-sage-green text-base sm:text-lg"></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-dark-charcoal text-sm sm:text-base">Email</h3>
                      <a
                        href="mailto:info.vestrioz@gmail.com"
                        className="text-sage-green hover:text-dark-charcoal transition-colors duration-200 text-sm sm:text-base break-all"
                      >
                        info.vestrioz@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-sage-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-clock text-sage-green text-base sm:text-lg"></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-dark-charcoal text-sm sm:text-base">Business Hours</h3>
                      <p className="text-medium-gray text-sm sm:text-base">Monday – Saturday: 10:00 AM to 7:00 PM (IST)</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-sage-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-map-marker-alt text-sage-green text-base sm:text-lg"></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-dark-charcoal text-sm sm:text-base">Registered Address</h3>
                      <p className="text-medium-gray text-sm sm:text-base">VestrioZ<br />Surat, Gujarat<br />India</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Support Notice */}
              <div className="bg-sage-green/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-sage-green/20">
                <div className="flex items-start space-x-3">
                  <i className="fas fa-info-circle text-sage-green text-lg sm:text-xl mt-1 flex-shrink-0"></i>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-dark-charcoal mb-2 text-sm sm:text-base">Support Response Time</h3>
                    <p className="text-medium-gray text-sm sm:text-base">
                      You can contact us anytime via email, and our support team will respond
                      within <strong>24–48 working hours</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 border border-sage-green/10">
              <h2 className="text-xl sm:text-2xl font-semibold text-dark-charcoal mb-4 sm:mb-6">
                Send us a Message
              </h2>

              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-check-circle text-green-600 text-xl mt-0.5"></i>
                    <div>
                      <h3 className="font-semibold text-green-800 mb-1">Message Sent Successfully!</h3>
                      <p className="text-green-700 text-sm">
                        Thank you for your message! We will respond within 24-48 working hours.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-exclamation-circle text-red-600 text-xl mt-0.5"></i>
                    <div>
                      <h3 className="font-semibold text-red-800 mb-1">Failed to Send Message</h3>
                      <p className="text-red-700 text-sm">
                        There was an error sending your message. Please try again or contact us directly at info.vestrioz@gmail.com
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-dark-charcoal mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-sage-green focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${validationErrors.name ? 'border-red-500' : 'border-sage-green/30'
                      }`}
                    placeholder="Enter your full name"
                  />
                  {validationErrors.name && (
                    <p className="mt-1 text-sm text-red-600">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      {validationErrors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-dark-charcoal mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-sage-green focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${validationErrors.email ? 'border-red-500' : 'border-sage-green/30'
                      }`}
                    placeholder="Enter your email address"
                  />
                  {validationErrors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      {validationErrors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-dark-charcoal mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-sage-green focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${validationErrors.subject ? 'border-red-500' : 'border-sage-green/30'
                      }`}
                  >
                    <option value="">Select a subject</option>
                    <option value="rental-support">Rental Support</option>
                    <option value="listing-help">Listing Help</option>
                    <option value="payment-issue">Payment Issue</option>
                    <option value="refund-request">Refund Request</option>
                    <option value="technical-issue">Technical Issue</option>
                    <option value="general-inquiry">General Inquiry</option>
                    <option value="other">Other</option>
                  </select>
                  {validationErrors.subject && (
                    <p className="mt-1 text-sm text-red-600">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      {validationErrors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-dark-charcoal mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    rows={5}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-sage-green focus:border-transparent transition-all duration-200 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed ${validationErrors.message ? 'border-red-500' : 'border-sage-green/30'
                      }`}
                    placeholder="Please describe your question or concern in detail..."
                  />
                  {validationErrors.message && (
                    <p className="mt-1 text-sm text-red-600">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      {validationErrors.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sage-green text-white py-2 sm:py-3 px-4 sm:px-6 rounded-lg font-semibold text-sm sm:text-base hover:bg-sage-green/90 transition-all duration-200 transform hover:scale-[1.02] focus:ring-2 focus:ring-sage-green focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-2"></i>
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default ContactUs;